import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Users, CheckCircle2, Search, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { DateInput } from "@/components/DateInput";

const TUR_OPTIONS = [
  { v: "yillik",        l: "🏖️ Yıllık İzin" },
  { v: "ucretli_izin",  l: "💰 Ücretli İzin" },
  { v: "rapor",         l: "🏥 Rapor" },
  { v: "ucretsiz_izin", l: "⚪ Ücretsiz İzin" },
];

export default function BulkLeaveModal({ onClose, onSaved }) {
  const [personnel, setPersonnel] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [posFilter, setPosFilter] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    baslangic: today,
    bitis: today,
    tur: "yillik",
    is_gunu_modu: true,
    aciklama: "",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/personnel?aktif=true");
        setPersonnel(r.data || []);
      } catch { toast.error("Personel listesi yüklenemedi"); }
    })();
  }, []);

  const positions = useMemo(() => {
    const set = new Set();
    personnel.forEach((p) => { if (p.pozisyon) set.add(p.pozisyon); });
    return [...set].sort();
  }, [personnel]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return personnel.filter((p) => {
      if (posFilter && p.pozisyon !== posFilter) return false;
      if (!q) return true;
      return (p.ad_soyad || "").toLowerCase().includes(q) ||
             (p.sicil_no || "").toLowerCase().includes(q);
    });
  }, [personnel, search, posFilter]);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const toggleAllFiltered = () => {
    const allSelected = filtered.every((p) => selectedIds.has(p.id));
    setSelectedIds((prev) => {
      const n = new Set(prev);
      filtered.forEach((p) => allSelected ? n.delete(p.id) : n.add(p.id));
      return n;
    });
  };

  const estimateDays = useMemo(() => {
    if (!form.baslangic || !form.bitis) return 0;
    const s = new Date(form.baslangic + "T00:00:00");
    const e = new Date(form.bitis + "T00:00:00");
    if (e < s) return 0;
    const cal = Math.round((e - s) / 86400000) + 1;
    if (!form.is_gunu_modu) return cal;
    // Rough estimate without server-side holidays
    let n = 0;
    const cur = new Date(s);
    for (let i = 0; i < cal; i++) {
      const w = cur.getDay();
      if (w >= 1 && w <= 5) n += 1;
      cur.setDate(cur.getDate() + 1);
    }
    return n;
  }, [form.baslangic, form.bitis, form.is_gunu_modu]);

  const submit = async () => {
    if (selectedIds.size === 0) { toast.error("En az bir personel seçin"); return; }
    if (!form.baslangic || !form.bitis) { toast.error("Tarih seçin"); return; }
    if (form.bitis < form.baslangic) { toast.error("Bitiş, başlangıçtan önce olamaz"); return; }
    setBusy(true);
    try {
      const r = await api.post("/annual-leave/bulk", {
        personnel_ids: [...selectedIds],
        baslangic: form.baslangic,
        bitis: form.bitis,
        tur: form.tur,
        is_gunu_modu: form.is_gunu_modu,
        aciklama: form.aciklama || "",
      });
      setResult(r.data);
      toast.success(`${r.data.created_count} personel için izin oluşturuldu`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toplu izin oluşturulamadı");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="bulk-leave-modal">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-4xl flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 shrink-0">
          <h3 className="font-display font-extrabold text-slate-900 flex items-center gap-2">
            <Users size={18} className="text-indigo-600"/>
            Toplu İzin Girişi
            <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
              {selectedIds.size} personel seçili
            </span>
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={16}/></button>
        </div>

        <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-5 overflow-y-auto flex-1">
          {/* LEFT — personnel multi-select */}
          <div className="space-y-2">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-700">Personel Seçimi</div>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="İsim veya sicil ara..."
                  data-testid="bulk-search"
                  className="w-full pl-7 pr-2 py-1.5 border border-slate-300 rounded-md text-sm"/>
              </div>
              <select value={posFilter} onChange={(e) => setPosFilter(e.target.value)}
                data-testid="bulk-pos-filter"
                className="px-2 py-1.5 border border-slate-300 rounded-md text-sm">
                <option value="">Tüm pozisyonlar</option>
                {positions.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="flex items-center justify-between text-xs">
              <button onClick={toggleAllFiltered} className="text-indigo-700 hover:text-indigo-900 font-semibold" data-testid="bulk-toggle-all">
                {filtered.every((p) => selectedIds.has(p.id)) && filtered.length > 0 ? "Hepsini bırak" : "Hepsini seç"} ({filtered.length})
              </button>
              <button onClick={() => setSelectedIds(new Set())} className="text-slate-500 hover:text-slate-900">
                Seçimi temizle
              </button>
            </div>
            <div className="border border-slate-200 rounded-md max-h-80 overflow-y-auto bg-white">
              {filtered.map((p) => {
                const on = selectedIds.has(p.id);
                return (
                  <label key={p.id}
                    data-testid={`bulk-pers-${p.id}`}
                    className={`flex items-center gap-2 px-2 py-1.5 border-b border-slate-100 last:border-b-0 cursor-pointer text-xs ${on ? "bg-indigo-50" : "hover:bg-slate-50"}`}>
                    <input type="checkbox" checked={on} onChange={() => toggle(p.id)}/>
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900">{p.ad_soyad}</div>
                      <div className="text-[10px] text-slate-500">{p.pozisyon} · {p.sicil_no}</div>
                    </div>
                  </label>
                );
              })}
              {filtered.length === 0 && <div className="text-center text-slate-400 py-4 text-xs italic">Eşleşen personel yok</div>}
            </div>
          </div>

          {/* RIGHT — form */}
          <div className="space-y-3">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-700">İzin Detayları</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-600 block mb-1">Başlangıç</label>
                <DateInput value={form.baslangic}
                  onChange={(e) => { setForm({ ...form, baslangic: e.target.value, bitis: form.bitis < e.target.value ? e.target.value : form.bitis }); }}
                  data-testid="bulk-start"
                  className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm font-mono"/>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-600 block mb-1">Bitiş</label>
                <DateInput value={form.bitis} min={form.baslangic}
                  onChange={(e) => setForm({ ...form, bitis: e.target.value })}
                  data-testid="bulk-end"
                  className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm font-mono"/>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600 block mb-1">İzin Türü</label>
              <select value={form.tur} onChange={(e) => setForm({ ...form, tur: e.target.value })}
                data-testid="bulk-tur"
                className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm">
                {TUR_OPTIONS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </div>
            <label className="flex items-start gap-2 bg-sky-50 border border-sky-200 rounded-md px-2.5 py-2 cursor-pointer text-xs">
              <input type="checkbox" checked={form.is_gunu_modu} onChange={(e) => setForm({ ...form, is_gunu_modu: e.target.checked })} data-testid="bulk-isgunu"/>
              <div>
                <strong>İş Günü Modu</strong>
                <div className="text-slate-500">Hafta sonu ve resmi tatiller düşülür.</div>
              </div>
            </label>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600 block mb-1">Açıklama <span className="text-slate-400">(opsiyonel)</span></label>
              <input type="text" value={form.aciklama} onChange={(e) => setForm({ ...form, aciklama: e.target.value })}
                placeholder="Örn: Yılbaşı tatili"
                data-testid="bulk-aciklama"
                className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm"/>
            </div>

            <div className="bg-indigo-50 border border-indigo-200 rounded-md px-3 py-2 text-xs text-indigo-900">
              <div className="flex items-center justify-between">
                <span>Her personel için tahmini</span>
                <strong className="font-mono text-lg">{estimateDays} gün</strong>
              </div>
              <div className="text-[10px] text-indigo-700 mt-1">
                Toplam: {selectedIds.size} personel × {estimateDays}g = <strong>{(selectedIds.size * estimateDays).toLocaleString("tr-TR")}</strong> gün kaydı
              </div>
            </div>

            {result && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-xs text-emerald-900 flex items-start gap-2">
                <CheckCircle2 size={14} className="mt-0.5 shrink-0"/>
                <div>
                  <strong>{result.created_count} izin kaydı oluşturuldu</strong>
                  {result.missing_count > 0 && <div className="text-amber-700 mt-0.5">{result.missing_count} personel bulunamadı</div>}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 shrink-0">
          <button onClick={onClose} className="px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-md">
            {result ? "Kapat" : "İptal"}
          </button>
          {!result && (
            <button onClick={submit} disabled={busy || selectedIds.size === 0}
              data-testid="bulk-submit"
              className="px-4 py-2 text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-md disabled:opacity-50 inline-flex items-center gap-1.5">
              {busy ? <Loader2 size={13} className="animate-spin"/> : <CheckCircle2 size={14}/>}
              {selectedIds.size} Kişiye İzin Oluştur
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
